import {
  addPrefixToKeys,
  filterObjectOnKeyContaining,
  removeNullValues,
  removePrefixFromObjectKeys,
} from "@/helpers/object-helpers";
import type { LocationQuery } from "vue-router";
import { DraftSortDirection, DraftSortFields } from "@/core/api/data-contracts";
import type { DraftListOptions } from "./types";

// Facets share the work-order list's URL convention: each selected facet is stored in the route
// query as `facet-<name>` so it can't collide with the other draft list params.
const FACET_PREFIX = "facet-";

/** Rows per "Visa fler", and what a new search, filter or sort starts from. */
export const DRAFT_LIST_PAGE_SIZE = 50;

export const defaultDraftListParameters: DraftListOptions = {
  searchTerm: null,
  officeId: [],
  showOnlyOwn: true,
  showNotAssigned: false,
  cscAllRegion: false,
  facet: {},
  sortDirection: DraftSortDirection.Descending,
  sortField: DraftSortFields.CreatedDate,
  offset: 0,
  limit: DRAFT_LIST_PAGE_SIZE,
};

/** The `status` facet's values (WorkOrderFacets.DraftFacetDefinitions). None selected = the open ärenden. */
export const draftStatus = {
  new: "New",
  handle: "InProgress",
  waiting: "Waiting",
  promoted: "Promoted",
  cancelled: "Cancelled",
} as const;

/**
 * Reads the `facet-<name>` entries from a route query into a `{ [name]: string[] }` map. The router gives
 * a param that appears once as a string (`?facet-status=New`), so single values are wrapped: callers
 * like the "Nya ärenden" chip call array methods on them.
 */
export function makeDraftFacetFromRoute(query: LocationQuery): Record<string, string[]> {
  const prefixed = filterObjectOnKeyContaining(query, FACET_PREFIX);
  const facet = removePrefixFromObjectKeys(prefixed, FACET_PREFIX) as Record<string, string | string[]>;
  return Object.fromEntries(Object.entries(facet).map(([name, values]) => [name, [values].flat()]));
}

export function makeDraftListApiParams(draftOptions: DraftListOptions) {
  const { cscAllRegion, officeId, ...rest } = draftOptions;
  return {
    ...rest,
    ...(officeId.length ? { officeId } : {}),
    ...(cscAllRegion ? { cscAllRegion: 1 } : {}),
  };
}

function draftListFilterQueryParameters(draftOptions: DraftListOptions) {
  return {
    searchTerm: draftOptions.searchTerm,
    officeId: toRouteQueryParam(draftOptions.officeId),
    mine: draftOptions.showOnlyOwn ? "1" : "0",
    notassigned: draftOptions.showNotAssigned ? "1" : undefined,
    cscAllRegion: draftOptions.cscAllRegion ? "1" : undefined,
    ...addPrefixToKeys(draftOptions.facet ?? {}, FACET_PREFIX),
  };
}

/** Query string for list filters only (no offset/limit/sort). Use for deep links that should reset paging. */
export function makeRouterQueryFromDraftListFiltersOnly(draftOptions: DraftListOptions) {
  return removeNullValues(draftListFilterQueryParameters(draftOptions));
}

export function makeRouterQueryFromDraftList(draftOptions: DraftListOptions) {
  const queryParameters = {
    ...draftListFilterQueryParameters(draftOptions),
    sortDirection: draftOptions.sortDirection?.toString(),
    sortField: draftOptions.sortField?.toString(),
    offset: draftOptions.offset?.toString(),
    limit: draftOptions.limit?.toString(),
  };
  return removeNullValues(queryParameters);
}

/** Match Vue Router query shape: omit empty, string for one value, array for many. */
function toRouteQueryParam(values: string[]): string | string[] | undefined {
  if (values.length === 0) {
    return undefined;
  }
  if (values.length === 1) {
    return values[0];
  }
  return values;
}
